import React, { useEffect, useRef } from "react";
import { View, StyleSheet, Text, TouchableOpacity, ScrollView } from "react-native";
import Icons from "@expo/vector-icons/MaterialCommunityIcons";
import { useSelector, useDispatch } from "react-redux";
import useColorScheme from "../../hooks/useColorScheme";
import {
    getAllNotifications,
    readAllNotifications,
    getAllNotificationsCount,
} from "../../redux/dispatcher/notificationDispatcher";

export default function NotificationMenu(props: any) {
    const { notifications, loading } = useSelector((state: any) => state.notification);
    const colorScheme = useColorScheme();
    const dispatch = useDispatch<any>();
    const outOfBoxRef = useRef(null);

    const checkOutsideClick = (ref: any) => {
        useEffect(() => {
            let isMounted = true;
            function handleClickOutside(event: any) {
                // If click is inside the menu itself, do nothing
                if (ref.current && ref.current.contains(event.target)) return;

                // If click is inside the trigger button, do nothing (let the button's onPress toggle it)
                if (props.buttonRef?.current && props.buttonRef.current.contains(event.target)) return;

                // Otherwise it's a click outside, so close the menu
                if (isMounted && props.onClose) {
                    props.onClose();
                }
            }

            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
                isMounted = false;
            };
        }, [ref]);
    };

    checkOutsideClick(outOfBoxRef);

    useEffect(() => {
        // Optionally fetch local notifications on mount if needed.
        // Assuming root navigator is handling the socket live updates,
        // we can just trigger a fetch here to ensure data is fresh.
        dispatch(
            getAllNotifications({
                page: 1,
                pageSize: 5,
                sort: { key: "createdAt", direction: "desc" },
                status: "all",
            })
        );
    }, [dispatch]);

    const viewAllHandler = () => {
        if (props.onClose) {
            props.onClose();
        }
        // Navigate to notifications page
        props.navigation?.navigate("UserSettings", { screen: "Notification", params: { screen: "UserSettingNotificationScreen" } });
    };

    const markAllAsReadHandler = async () => {
        try {
            await dispatch(readAllNotifications()).unwrap();

            // Refresh notifications list and count
            dispatch(
                getAllNotifications({
                    page: 1,
                    pageSize: 5,
                    sort: { key: "createdAt", direction: "desc" },
                    status: "all",
                })
            );
            dispatch(getAllNotificationsCount());
        } catch (error) {
            console.error("Failed to mark all as read:", error);
        }
    };

    const selectedNotification = (notif: any) => {
        // Determine icon based on notification type
        let iconName = "information-outline";
        let iconColor = "#3b82f6"; // blue
        if (notif.type === "WARNING") {
            iconName = "alert-outline";
            iconColor = "#eab308"; // yellow
        } else if (notif.type === "SUCCESS") {
            iconName = "check-circle-outline";
            iconColor = "#22c55e"; // green
        } else if (notif.type === "ERROR") {
            iconName = "close-circle-outline";
            iconColor = "#ef4444"; // red
        }

        return (
            <View key={notif._id || Math.random().toString()} style={styles.notificationItem}>
                <View style={styles.notificationIconWrapper}>
                    <Icons name={iconName as any} size={20} color={iconColor} />
                </View>
                <View style={styles.notificationTextWrapper}>
                    <Text style={styles.notificationTitle} numberOfLines={1}>{notif.title || "Notification"}</Text>
                    <Text style={styles.notificationMessage} numberOfLines={2}>{notif.message}</Text>
                </View>
            </View>
        );
    };

    return (
        <View ref={outOfBoxRef} style={styles.container}>
            <View style={styles.header}>
                <Text style={styles.headerTitle}>Notifications</Text>
                <TouchableOpacity onPress={markAllAsReadHandler}>
                    <Text style={styles.markAllText}>Mark all as read</Text>
                </TouchableOpacity>
            </View>
            <View style={styles.divider} />

            <ScrollView style={styles.listContainer} showsVerticalScrollIndicator={false}>
                {loading ? (
                    <View style={styles.emptyContainer}>
                        <Text style={styles.emptyText}>Loading...</Text>
                    </View>
                ) : notifications && notifications.length > 0 ? (
                    notifications.slice(0, 5).map((notif: any) => selectedNotification(notif))
                ) : (
                    <View style={styles.emptyContainer}>
                        <Icons name="bell-off-outline" size={24} color="#9ca3af" />
                        <Text style={styles.emptyText}>No new notifications</Text>
                    </View>
                )}
            </ScrollView>

            <View style={styles.divider} />
            <TouchableOpacity style={styles.viewAllButton} onPress={viewAllHandler}>
                <Text style={styles.viewAllText}>View All</Text>
                <Icons name="chevron-right" size={16} color="#61B8FF" />
            </TouchableOpacity>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        minWidth: 320,
        maxWidth: 360,
        maxHeight: 400,
        paddingVertical: 8,
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: 16,
        paddingVertical: 10,
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: "700",
        color: "#111827",
    },
    markAllText: {
        fontSize: 13,
        color: "#2563EB",
        fontWeight: "600",
    },
    divider: {
        height: 1,
        backgroundColor: "#f3f4f6",
        marginHorizontal: 0,
    },
    listContainer: {
        maxHeight: 280,
    },
    notificationItem: {
        flexDirection: "row",
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: "#f9fafb",
    },
    notificationIconWrapper: {
        marginRight: 12,
        marginTop: 2,
    },
    notificationTextWrapper: {
        flex: 1,
    },
    notificationTitle: {
        fontSize: 14,
        fontWeight: "600",
        color: "#374151",
        marginBottom: 4,
    },
    notificationMessage: {
        fontSize: 13,
        color: "#6b7280",
        lineHeight: 18,
    },
    emptyContainer: {
        paddingVertical: 32,
        alignItems: "center",
        justifyContent: "center",
    },
    emptyText: {
        marginTop: 8,
        fontSize: 14,
        color: "#9ca3af",
    },
    viewAllButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 12,
    },
    viewAllText: {
        fontSize: 14,
        fontWeight: "600",
        color: "#61B8FF",
        marginRight: 4,
    },
});
